import React, { useState, useEffect } from 'react';
import { Link, Info, Building, Shield, CheckCircle2 } from 'lucide-react';
import FormModal from './FormModal';

interface Framework {
  id: number;
  name: string;
  description?: string;
  activeVersionId: number;
  activeVersionName: string;
}

interface Company {
  id: number;
  name: string;
}

interface FrameworkAssignment {
  id: number;
  frameworkId: number;
  companyId: number;
  companyName: string;
  frameworkName: string;
  frameworkVersionName?: string;
  status: 'pending' | 'active' | 'completed' | 'archived';
  notes?: string;
  [key: string]: any;
}

interface FrameworkAssignmentModalProps {
  onClose: () => void;
  onSubmit: (assignment: Partial<FrameworkAssignment>) => Promise<void> | void;
  selectedAssignment: FrameworkAssignment | null;
  companies?: Company[];
  frameworks?: Framework[];
}

export const FrameworkAssignmentModal: React.FC<FrameworkAssignmentModalProps> = ({
  onClose,
  onSubmit,
  selectedAssignment,
  companies: propCompanies = [],
  frameworks: propFrameworks = [],
}) => {
  const [assignment, setAssignment] = useState<Partial<FrameworkAssignment>>(
    selectedAssignment || {
      frameworkId: 0,
      companyId: 0,
      status: 'active',
      notes: '',
    }
  );

  const [frameworks, setFrameworks] = useState<Framework[]>(propFrameworks);
  const [companies, setCompanies] = useState<Company[]>(propCompanies);
  const [loading, _setLoading] = useState(false);

  useEffect(() => {
    if (propFrameworks.length > 0) setFrameworks(propFrameworks);
    else loadFrameworks();
    if (propCompanies.length > 0) setCompanies(propCompanies);
    else loadCompanies();
  }, [propFrameworks, propCompanies]);

  const loadFrameworks = async () => {
    try {
      const response = await fetch('/api/compliance/definition/frameworks/assignable', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const result = await response.json();
        setFrameworks(result.data || []);
      }
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch('/api/companies?limit=500&sortBy=createdAt&sortOrder=desc', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const result = await response.json();
        setCompanies(result.data || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(assignment);
      onClose();
    } catch {
      // error handled by parent
    }
  };

  const isCreate = !selectedAssignment?.id;
  const selectedFramework = frameworks.find(f => f.id === assignment.frameworkId);

  return (
    <FormModal
      title={isCreate ? 'Assign Framework to Company' : 'Edit Framework Assignment'}
      subtitle={isCreate
        ? 'This will generate compliance pages the company must complete'
        : 'Modify assignment status or notes'}
      icon={<Link className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Assign Framework' : 'Update Assignment'}
      cancelText="Cancel"
      loading={loading}
    >
      <div className="space-y-6">
        {/* What happens info box */}
        {isCreate && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800">
                <p className="font-semibold mb-1">What happens when you assign a framework?</p>
                <ul className="space-y-0.5 text-blue-700">
                  <li>• Compliance pages are <strong>automatically generated</strong> for every mapped requirement</li>
                  <li>• The company can then fill in evidence and submit pages for review</li>
                  <li>• Only frameworks with published templates and mappings appear here</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Company selector */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            <Building size={14} className="inline mr-1" />
            Company <span className="text-red-500">*</span>
          </label>
          {selectedAssignment?.id ? (
            <div className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-slate-50 text-slate-700">
              {selectedAssignment.companyName} (locked)
            </div>
          ) : (
            <>
              <select
                value={assignment.companyId || 0}
                onChange={(e) => setAssignment({ ...assignment, companyId: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value={0}>Select a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400">The company whose team will complete the compliance requirements</p>
            </>
          )}
        </div>

        {/* Framework selector */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            <Shield size={14} className="inline mr-1" />
            Framework <span className="text-red-500">*</span>
          </label>
          {selectedAssignment?.id ? (
            <div className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-slate-50 text-slate-700">
              {selectedAssignment.frameworkName} — Version {selectedAssignment.frameworkVersionName} (locked)
            </div>
          ) : frameworks.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-800 font-medium">No frameworks ready for assignment</p>
              <p className="text-xs text-amber-600 mt-1">
                To assign a framework, you need to: create a framework with an active version, build and publish a template, and map the template to the framework.
              </p>
            </div>
          ) : (
            <>
              <select
                value={assignment.frameworkId || 0}
                onChange={(e) => {
                  const frameworkId = parseInt(e.target.value);
                  const fw = frameworks.find(f => f.id === frameworkId);
                  setAssignment({
                    ...assignment,
                    frameworkId,
                    ...(fw?.activeVersionId && { frameworkVersionId: fw.activeVersionId }),
                    ...(fw?.activeVersionName && { frameworkVersionName: fw.activeVersionName }),
                  });
                }}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value={0}>Select a framework...</option>
                {frameworks.map(framework => (
                  <option key={framework.id} value={framework.id}>
                    {framework.name} — Version {framework.activeVersionName}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400">Only frameworks that are fully set up (template published + mapped) appear here</p>
            </>
          )}
          {selectedFramework && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <CheckCircle2 size={12} />
              Version {selectedFramework.activeVersionName} will be used
            </div>
          )}
        </div>

        {/* Status — only show for edit mode, auto-set for create */}
        {selectedAssignment?.id && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Status</label>
            <select
              value={assignment.status || 'active'}
              onChange={(e) => setAssignment({ ...assignment, status: e.target.value as any })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="active">Active — Company is working on compliance</option>
              <option value="pending">Pending — Assignment created but not started</option>
              <option value="completed">Completed — All requirements fulfilled</option>
              <option value="archived">Archived — No longer active</option>
            </select>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Notes (optional)</label>
          <textarea
            value={assignment.notes || ''}
            onChange={(e) => setAssignment({ ...assignment, notes: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            rows={2}
            placeholder="e.g., Priority compliance for Q2 audit..."
            disabled={loading}
          />
        </div>
      </div>
    </FormModal>
  );
};
